import React, { useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Helmet } from "react-helmet";
import { useQuery, useMutation } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import CustomLoader from "app/components/common/CustomLoader";
import { userPrimaryRoleSelector } from "app/modules/schools/selectors";
import { ADMIN_ROLES } from "app/config/settingsidebar";
import {
  getWhatsAppProvider,
  upsertWhatsAppProviderName,
} from "app/services/schoolService/com/wa.service";

// must match the enum on Third_Party_Config.whatsAppProvider
const PROVIDERS = [
  {
    value: "EsChat",
    label: "EsChat",
    description: "Send WhatsApp messages through the EsChat business account",
  },
  {
    value: "Veblika",
    label: "Veblika",
    description: "Send WhatsApp messages through the Veblika business account",
  },
];

const WhatsAppProvider = () => {
  const [provider, setProvider] = useState("");
  const [savedProvider, setSavedProvider] = useState("");
  const primaryRole = useSelector(userPrimaryRoleSelector);
  const isAdmin = ADMIN_ROLES.includes(primaryRole);

  const {
    isLoading: providerLoading,
    isFetching: providerFetching,
    refetch: refetchProvider,
  } = useQuery({
    queryKey: ["getWhatsAppProvider"],
    queryFn: () => getWhatsAppProvider(),
    enabled: isAdmin,
    onSuccess: (res) => {
      const value = res?.data?.obj?.whatsAppProvider || "";
      setProvider(value);
      setSavedProvider(value);
    },
    onError: (error) => {
      // 404 means no provider has been configured yet for this school
      if (error?.response?.status === 404) {
        setProvider("");
        setSavedProvider("");
        return;
      }
      toast.error(
        error?.response?.data?.message || "Failed to load WhatsApp provider"
      );
    },
  });

  const { mutate: saveProvider, isLoading: saveLoading } = useMutation(
    upsertWhatsAppProviderName,
    {
      onSuccess: (res) => {
        toast.success(
          res?.data?.message || "WhatsApp Provider updated successfully"
        );
        refetchProvider();
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message || "Failed to update WhatsApp provider"
        );
      },
    }
  );

  const isChanged = provider !== savedProvider;

  const handleSave = () => {
    saveProvider({ whatsAppProvider: provider });
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
        Only Admin can manage the WhatsApp provider.
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>WhatsApp Provider</title>
      </Helmet>

      <CustomLoader show={providerLoading || providerFetching} />

      <Box sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          WhatsApp Provider
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose which provider is used to send WhatsApp messages for this
          school.
          {!savedProvider && " No provider is configured yet."}
        </Typography>

        <RadioGroup
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
        >
          <Stack gap={2}>
            {PROVIDERS.map((item) => (
              <Paper
                key={item.value}
                variant="outlined"
                sx={{
                  px: 2,
                  py: 1,
                  borderColor:
                    provider === item.value ? "secondary.main" : "divider",
                }}
              >
                <FormControlLabel
                  value={item.value}
                  control={<Radio color="secondary" />}
                  sx={{ width: "100%", m: 0 }}
                  label={
                    <Box>
                      <Typography fontWeight={600}>
                        {item.label}
                        {savedProvider === item.value && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="success.main"
                            sx={{ ml: 1 }}
                          >
                            (Current)
                          </Typography>
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.description}
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            ))}
          </Stack>
        </RadioGroup>

        <Stack direction="row" gap={2} justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            color="secondary"
            size="large"
            onClick={() => setProvider(savedProvider)}
            disabled={!isChanged || saveLoading}
          >
            Clear
          </Button>
          <LoadingButton
            variant="contained"
            color="secondary"
            size="large"
            onClick={handleSave}
            disabled={!isChanged || !provider}
            loading={saveLoading}
          >
            Save
          </LoadingButton>
        </Stack>
      </Box>
    </>
  );
};

export default WhatsAppProvider;
